import type { CSSProperties } from 'react';

export interface AnchoredMenuOptions {
  /** Widest the menu can render (its CSS max-width), used to pick a side. */
  width: number;
  /** Tallest the menu can render, used to pick above or below. */
  height: number;
  /** Distance between the trigger and the menu. */
  gap?: number;
  /** Smallest distance the menu may keep from the viewport edges. */
  edge?: number;
  /** Defaults to the window; supplied by tests, which have no DOM. */
  viewport?: { width: number; height: number };
}

/**
 * Fixed-position style that pins a dropdown to its trigger and keeps it inside
 * the viewport: it flips above the trigger when space below is tight, and grows
 * leftward from the trigger's right edge unless the left side is too narrow.
 *
 * The result anchors CSS edges (`top`/`bottom` plus `right`/`left`) instead of a
 * computed origin, so the menu still lines up with the trigger when its real
 * size differs from the `width`/`height` estimates — those only decide which
 * side to open on.
 *
 * Callers must pass a rect measured from the live trigger. A rect captured
 * while the menu is laid out in the document flow is unusable: an in-flow menu
 * resizes its own container and moves the trigger it was measured from.
 *
 * Stacking is deliberately left out: the menu's own stylesheet owns its
 * `z-index`, so it can be expressed with the `--z-*` tokens.
 */
export function anchoredMenuPosition(
  anchor: DOMRect,
  { width, height, gap = 4, edge = 8, viewport }: AnchoredMenuOptions,
): CSSProperties {
  const vw = viewport?.width ?? window.innerWidth;
  const vh = viewport?.height ?? window.innerHeight;
  const style: CSSProperties = { position: 'fixed' };

  // Highest the menu's own top/bottom edge may sit and stay fully on screen.
  const verticalLimit = Math.max(vh - height - edge, edge);
  const spaceBelow = vh - anchor.bottom - gap;
  const spaceAbove = anchor.top - gap;
  if (spaceBelow >= height || spaceBelow >= spaceAbove) {
    style.top = Math.min(anchor.bottom + gap, verticalLimit);
  } else {
    style.bottom = Math.min(vh - anchor.top + gap, verticalLimit);
  }

  if (anchor.right - width >= edge) {
    style.right = Math.max(vw - anchor.right, edge);
  } else if (anchor.left + width <= vw - edge) {
    style.left = Math.max(anchor.left, edge);
  } else {
    // Neither side fits the menu, so pin it to the left edge and let it span.
    style.left = edge;
  }

  return style;
}
